import { get, ref, set, update } from "firebase/database";
import { normalizeNavGrid } from "./navGrid.js";
import { logger } from "./logger.js";

const CLOUD_META_PATH = "navGridMeta";
const CLOUD_SEGMENTS_PATH = "navGridSegments";
const CLOUD_INDEX_PATH = "navGridSegmentIndex";
const CLOUD_LEGACY_PATH = "navGrid";
const SEGMENT_FETCH_CHUNK = 30;
const NAVGRID_DEBUG_KEY = "trekkin.navgrid.debug";

function isDebugEnabled() {
  if (typeof window === "undefined") return true;
  try {
    const raw = String(window.localStorage?.getItem?.(NAVGRID_DEBUG_KEY) ?? "").trim().toLowerCase();
    if (!raw) return true;
    return raw === "1" || raw === "true" || raw === "on";
  } catch {
    return true;
  }
}

function navGridLog(level, message, payload = null) {
  const lvl = String(level || "debug").toLowerCase();
  if (lvl === "debug" && !isDebugEnabled()) return;
  const msg = String(message || "navgrid");
  if (lvl === "error") logger.error("NAVGRID_CLOUD", msg, payload || null);
  else if (lvl === "warn") logger.warn("NAVGRID_CLOUD", msg, payload || null);
  else if (lvl === "info") logger.info("NAVGRID_CLOUD", msg, payload || null);
  else logger.debug("NAVGRID_CLOUD", msg, payload || null);
}

function toPositiveNumber(value, fallback = 0) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return fallback;
  return num;
}

function toSegmentStamp(segment, fallbackTs) {
  const direct = toPositiveNumber(segment?.updatedAt, 0);
  if (direct) return direct;
  const created = toPositiveNumber(segment?.createdAt, 0);
  if (created) return created;
  return toPositiveNumber(fallbackTs, Date.now());
}

function computeSegmentSignature(segment) {
  if (!segment || typeof segment !== "object") return "seg:empty";
  const id = String(segment?.id || "");
  const name = String(segment?.name || "");
  const premium = segment?.premium === true ? 1 : 0;
  const activityMode = String(segment?.activityMode || "walk");
  const pts = Array.isArray(segment?.points) ? segment.points : [];
  let hash = 2166136261; // FNV-1a 32-bit offset basis
  const mix = (str) => {
    const s = String(str);
    for (let i = 0; i < s.length; i += 1) {
      hash ^= s.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
  };
  mix(id);
  mix(name);
  mix(premium);
  mix(activityMode);
  mix(pts.length);
  for (const p of pts) {
    const lat = Number.isFinite(Number(p?.lat))
      ? Math.round(Number(p.lat) * 1e6)
      : "x";
    const lon = Number.isFinite(Number(p?.lon ?? p?.lng))
      ? Math.round(Number(p.lon ?? p.lng) * 1e6)
      : "x";
    const ele = Number.isFinite(Number(p?.ele ?? p?.alt ?? p?.elevation))
      ? Math.round(Number(p.ele ?? p.alt ?? p.elevation) * 10)
      : "x";
    mix(lat);
    mix(lon);
    mix(ele);
  }
  return `h${(hash >>> 0).toString(16)}`;
}

function chunkArray(arr, size) {
  const out = [];
  if (!Array.isArray(arr) || !arr.length) return out;
  const safeSize = Math.max(1, Number(size) || 1);
  for (let i = 0; i < arr.length; i += safeSize) {
    out.push(arr.slice(i, i + safeSize));
  }
  return out;
}

function stripUndefinedDeep(value) {
  if (Array.isArray(value)) {
    const arr = value.map((item) => stripUndefinedDeep(item));
    return arr.filter((item) => item !== undefined);
  }
  if (value && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const cleaned = stripUndefinedDeep(v);
      if (cleaned !== undefined) out[k] = cleaned;
    }
    return out;
  }
  if (value === undefined) return undefined;
  return value;
}

function normalizeGridForCloud(rawGrid) {
  const normalized = normalizeNavGrid(rawGrid || {});
  const now = Date.now();
  const gridUpdatedAt = toPositiveNumber(normalized.updatedAt, now);
  const segments = Array.isArray(normalized.segments)
    ? normalized.segments.map((seg, idx) => {
        const id = String(seg?.id || `seg-${gridUpdatedAt}-${idx}`);
        return stripUndefinedDeep({
          ...(seg || {}),
          id,
          updatedAt: toSegmentStamp(seg, gridUpdatedAt),
        });
      })
    : [];

  const prepared = {
    ...normalized,
    segments,
    updatedAt: gridUpdatedAt,
  };

  const segmentsMap = {};
  const index = {};
  for (const seg of segments) {
    segmentsMap[seg.id] = seg;
    index[seg.id] = {
      updatedAt: seg.updatedAt,
      sig: computeSegmentSignature(seg),
    };
  }

  const meta = {
    version: 1,
    updatedAt: prepared.updatedAt,
    spacingMeters: toPositiveNumber(prepared.spacingMeters, 50),
    mergeMeters: toPositiveNumber(prepared.mergeMeters, 15),
    routeMergeMeters: toPositiveNumber(
      prepared.routeMergeMeters,
      Math.max(toPositiveNumber(prepared.mergeMeters, 15), 30)
    ),
    entryPromptMeters: Number(prepared.entryPromptMeters) || 200,
    segmentCount: segments.length,
  };

  return { grid: prepared, meta, segmentsMap, index };
}

function parseIndexValue(value) {
  if (value == null) return 0;
  if (typeof value === "object") {
    return toPositiveNumber(value.updatedAt, 0);
  }
  return toPositiveNumber(value, 0);
}

function parseIndexSignature(value) {
  if (!value || typeof value !== "object") return "";
  return String(value.sig || value.hash || "");
}

function buildLocalSegmentsMap(grid) {
  const out = {};
  const segments = Array.isArray(grid?.segments) ? grid.segments : [];
  for (const seg of segments) {
    const id = String(seg?.id || "");
    if (!id) continue;
    out[id] = seg;
  }
  return out;
}

async function fetchSegmentsByIds(db, ids) {
  const result = {};
  const chunks = chunkArray(ids, SEGMENT_FETCH_CHUNK);
  for (const chunk of chunks) {
    const rows = await Promise.all(
      chunk.map(async (id) => {
        try {
          const snap = await get(ref(db, `${CLOUD_SEGMENTS_PATH}/${id}`));
          if (!snap.exists()) return [id, null];
          return [id, snap.val()];
        } catch {
          return [id, null];
        }
      })
    );
    for (const [id, value] of rows) {
      result[id] = value;
    }
  }
  return result;
}

async function fetchLegacyGrid(db) {
  try {
    const snap = await get(ref(db, CLOUD_LEGACY_PATH));
    if (!snap.exists()) return null;
    return normalizeNavGrid(snap.val() || {});
  } catch {
    return null;
  }
}

export async function saveNavGridToCloud(db, grid, options = {}) {
  // O snapshot legado contém a grelha inteira (vários MB). Regravá-lo numa
  // alteração normal provoca um download integral em clientes antigos que
  // ainda escutem `/navGrid`. Só deve ser publicado por uma migração manual.
  const includeLegacy = options?.includeLegacy === true;
  const prepared = normalizeGridForCloud(grid);
  const currentIndexSnap = await get(ref(db, CLOUD_INDEX_PATH)).catch(() => null);
  const currentIndex =
    currentIndexSnap && currentIndexSnap.exists() && typeof currentIndexSnap.val() === "object"
      ? currentIndexSnap.val()
      : {};

  const nextIds = Object.keys(prepared.index || {});
  const currentIds = Object.keys(currentIndex || {});
  const changedIds = nextIds.filter((id) => {
    const prevEntry = currentIndex?.[id];
    const nextEntry = prepared.index?.[id];
    const prevStamp = parseIndexValue(prevEntry);
    const nextStamp = parseIndexValue(nextEntry);
    const prevSig = parseIndexSignature(prevEntry);
    const nextSig = parseIndexSignature(nextEntry);
    return prevStamp !== nextStamp || prevSig !== nextSig;
  });
  const removedIds = currentIds.filter(
    (id) => !Object.prototype.hasOwnProperty.call(prepared.index || {}, id)
  );

  const rootUpdates = {
    [CLOUD_META_PATH]: prepared.meta,
  };

  for (const id of changedIds) {
    rootUpdates[`${CLOUD_SEGMENTS_PATH}/${id}`] = prepared.segmentsMap[id] || null;
    rootUpdates[`${CLOUD_INDEX_PATH}/${id}`] = prepared.index[id] || null;
  }
  for (const id of removedIds) {
    rootUpdates[`${CLOUD_SEGMENTS_PATH}/${id}`] = null;
    rootUpdates[`${CLOUD_INDEX_PATH}/${id}`] = null;
  }

  const t0 = Date.now();
  navGridLog("info", "save start", {
    includeLegacy,
    segmentCount: nextIds.length,
    changedCount: changedIds.length,
    removedCount: removedIds.length,
    updatedAt: Number(prepared?.grid?.updatedAt || 0) || 0,
  });

  try {
    await update(ref(db), rootUpdates);
    navGridLog("info", "incremental update ok", {
      elapsedMs: Date.now() - t0,
      writes: Object.keys(rootUpdates).length,
    });
  } catch (err) {
    navGridLog("error", "incremental update failed", {
      error: String(err?.message || err || "unknown"),
      writes: Object.keys(rootUpdates).length,
    });
    throw err;
  }

  if (includeLegacy) {
    try {
      await set(ref(db, CLOUD_LEGACY_PATH), prepared.grid);
      navGridLog("info", "legacy snapshot ok", {
        elapsedMs: Date.now() - t0,
        segmentCount: Array.isArray(prepared?.grid?.segments) ? prepared.grid.segments.length : 0,
      });
    } catch (err) {
      navGridLog("error", "legacy snapshot failed", {
        error: String(err?.message || err || "unknown"),
      });
      throw err;
    }
  }

  return prepared.grid;
}

export async function saveNavGridSegmentToCloud(db, segment, grid = {}) {
  if (!segment || typeof segment !== "object") {
    throw new Error("navgrid_segment_invalid");
  }

  const normalizedGrid = normalizeNavGrid(grid || {});
  const now = Date.now();
  const id = String(segment?.id || "").trim();
  if (!id) throw new Error("navgrid_segment_id_missing");

  const preparedSegment = stripUndefinedDeep({
    ...segment,
    id,
    updatedAt: toSegmentStamp(segment, now),
  });
  const updatedAt = Math.max(
    toPositiveNumber(normalizedGrid.updatedAt, 0),
    toSegmentStamp(preparedSegment, now)
  );
  const indexEntry = {
    updatedAt: preparedSegment.updatedAt,
    sig: computeSegmentSignature(preparedSegment),
  };
  const rootUpdates = {
    [`${CLOUD_SEGMENTS_PATH}/${id}`]: preparedSegment,
    [`${CLOUD_INDEX_PATH}/${id}`]: indexEntry,
    [`${CLOUD_META_PATH}/updatedAt`]: updatedAt,
    [`${CLOUD_META_PATH}/spacingMeters`]: toPositiveNumber(normalizedGrid.spacingMeters, 50),
    [`${CLOUD_META_PATH}/mergeMeters`]: toPositiveNumber(normalizedGrid.mergeMeters, 15),
    [`${CLOUD_META_PATH}/routeMergeMeters`]: toPositiveNumber(
      normalizedGrid.routeMergeMeters,
      Math.max(toPositiveNumber(normalizedGrid.mergeMeters, 15), 30)
    ),
    [`${CLOUD_META_PATH}/entryPromptMeters`]: Number(normalizedGrid.entryPromptMeters) || 200,
    [`${CLOUD_META_PATH}/segmentCount`]: Number.isFinite(Number(grid?.segmentCount))
      ? Math.max(0, Number(grid.segmentCount))
      : Array.isArray(normalizedGrid.segments) ? normalizedGrid.segments.length : 0,
  };

  const t0 = Date.now();
  navGridLog("info", "segment upsert start", {
    segmentId: id,
    points: Array.isArray(preparedSegment?.points) ? preparedSegment.points.length : 0,
    updatedAt,
  });
  try {
    await update(ref(db), rootUpdates);
    navGridLog("info", "segment upsert ok", {
      segmentId: id,
      elapsedMs: Date.now() - t0,
    });
    return preparedSegment;
  } catch (err) {
    navGridLog("error", "segment upsert failed", {
      segmentId: id,
      error: String(err?.message || err || "unknown"),
    });
    throw err;
  }
}

export async function saveNavGridChangesToCloud(
  db,
  { upserts = [], removedIds = [], grid = {} } = {}
) {
  const normalizedGrid = normalizeNavGrid(grid || {});
  const now = Date.now();
  const uniqueUpserts = new Map();
  for (const segment of Array.isArray(upserts) ? upserts : []) {
    const id = String(segment?.id || "").trim();
    if (!id) continue;
    uniqueUpserts.set(id, stripUndefinedDeep({
      ...segment,
      id,
      updatedAt: Math.max(
        toSegmentStamp(segment, now),
        toPositiveNumber(normalizedGrid.updatedAt, now)
      ),
    }));
  }
  const removed = [...new Set(
    (Array.isArray(removedIds) ? removedIds : [])
      .map((id) => String(id || "").trim())
      .filter((id) => id && !uniqueUpserts.has(id))
  )];
  if (!uniqueUpserts.size && !removed.length) return true;

  const updatedAt = Math.max(toPositiveNumber(normalizedGrid.updatedAt, 0), now);
  const rootUpdates = {
    [`${CLOUD_META_PATH}/updatedAt`]: updatedAt,
    [`${CLOUD_META_PATH}/spacingMeters`]: toPositiveNumber(normalizedGrid.spacingMeters, 50),
    [`${CLOUD_META_PATH}/mergeMeters`]: toPositiveNumber(normalizedGrid.mergeMeters, 15),
    [`${CLOUD_META_PATH}/routeMergeMeters`]: toPositiveNumber(
      normalizedGrid.routeMergeMeters,
      Math.max(toPositiveNumber(normalizedGrid.mergeMeters, 15), 30)
    ),
    [`${CLOUD_META_PATH}/entryPromptMeters`]: Number(normalizedGrid.entryPromptMeters) || 200,
    [`${CLOUD_META_PATH}/segmentCount`]: Number.isFinite(Number(grid?.segmentCount))
      ? Math.max(0, Number(grid.segmentCount))
      : Array.isArray(normalizedGrid.segments) ? normalizedGrid.segments.length : 0,
  };
  for (const [id, segment] of uniqueUpserts) {
    rootUpdates[`${CLOUD_SEGMENTS_PATH}/${id}`] = segment;
    rootUpdates[`${CLOUD_INDEX_PATH}/${id}`] = {
      updatedAt: segment.updatedAt,
      sig: computeSegmentSignature(segment),
    };
  }
  for (const id of removed) {
    rootUpdates[`${CLOUD_SEGMENTS_PATH}/${id}`] = null;
    rootUpdates[`${CLOUD_INDEX_PATH}/${id}`] = null;
  }

  const t0 = Date.now();
  navGridLog("info", "edit patch start", {
    upserts: uniqueUpserts.size,
    removed: removed.length,
    writes: Object.keys(rootUpdates).length,
  });
  try {
    await update(ref(db), rootUpdates);
    navGridLog("info", "edit patch ok", {
      upserts: uniqueUpserts.size,
      removed: removed.length,
      elapsedMs: Date.now() - t0,
    });
    return true;
  } catch (err) {
    navGridLog("error", "edit patch failed", {
      upserts: uniqueUpserts.size,
      removed: removed.length,
      error: String(err?.message || err || "unknown"),
    });
    throw err;
  }
}

export async function saveNavGridDiffToCloud(db, previousGrid = {}, nextGrid = {}) {
  const previous = normalizeNavGrid(previousGrid || {});
  const next = normalizeNavGrid(nextGrid || {});
  const previousById = new Map(
    (Array.isArray(previous.segments) ? previous.segments : [])
      .filter((segment) => segment?.id)
      .map((segment) => [String(segment.id), segment])
  );
  const nextById = new Map(
    (Array.isArray(next.segments) ? next.segments : [])
      .filter((segment) => segment?.id)
      .map((segment) => [String(segment.id), segment])
  );
  const upserts = [];
  for (const [id, segment] of nextById) {
    const previousSegment = previousById.get(id);
    if (!previousSegment || previousSegment !== segment) {
      upserts.push(segment);
    }
  }
  const removedIds = [...previousById.keys()].filter((id) => !nextById.has(id));
  return saveNavGridChangesToCloud(db, { upserts, removedIds, grid: next });
}

export async function fetchNavGridFromCloud(db, localGrid, options = {}) {
  const force = options?.force === true;
  const local = normalizeNavGrid(localGrid || {});
  const localMap = buildLocalSegmentsMap(local);
  const localStamp = toPositiveNumber(local.updatedAt, 0);

  try {
    navGridLog("debug", "fetch start", {
      force,
      localSegments: Object.keys(localMap).length,
      localUpdatedAt: localStamp,
    });
    const [metaSnap, indexSnap] = await Promise.all([
      get(ref(db, CLOUD_META_PATH)),
      get(ref(db, CLOUD_INDEX_PATH)),
    ]);

    const metaVal = metaSnap.exists() ? metaSnap.val() : null;
    const indexVal = indexSnap.exists() ? indexSnap.val() : null;

    if (metaVal && indexVal && typeof indexVal === "object") {
      const remoteUpdatedAt = toPositiveNumber(metaVal.updatedAt, 0);
      const remoteIds = Object.keys(indexVal || {});

      const missingOrOutdated = [];
      const locallyNewerIds = [];
      for (const id of remoteIds) {
        const remoteEntry = indexVal[id];
        const remoteSegStamp = parseIndexValue(remoteEntry);
        const remoteSig = parseIndexSignature(remoteEntry);
        const localSeg = localMap[id];
        const localSegStamp = toSegmentStamp(localSeg, 0);
        const localSig = computeSegmentSignature(localSeg);
        if (localSeg && localSegStamp > remoteSegStamp) {
          locallyNewerIds.push(id);
          continue;
        }
        if (
          !localSeg ||
          localSegStamp < remoteSegStamp ||
          (remoteSig && localSig !== remoteSig)
        ) {
          missingOrOutdated.push(id);
        }
      }

      const removedIds = [];
      const retainedLocalIds = [...locallyNewerIds];
      for (const id of Object.keys(localMap)) {
        if (Object.prototype.hasOwnProperty.call(indexVal, id)) continue;
        const localSegmentStamp = toSegmentStamp(localMap[id], 0);
        if (localSegmentStamp > remoteUpdatedAt) retainedLocalIds.push(id);
        else removedIds.push(id);
      }

      const noDiff =
        missingOrOutdated.length === 0 &&
        removedIds.length === 0 &&
        retainedLocalIds.length === 0 &&
        remoteUpdatedAt <= localStamp;

      if (!force && noDiff) {
        navGridLog("debug", "fetch no diff", {
          remoteUpdatedAt,
          localUpdatedAt: localStamp,
          remoteSegments: remoteIds.length,
        });
        return {
          grid: local,
          changed: false,
          source: "incremental",
          stats: { fetchedSegments: 0, removedSegments: 0 },
        };
      }

      const fetched = await fetchSegmentsByIds(db, missingOrOutdated);
      const merged = { ...localMap };

      for (const id of removedIds) {
        delete merged[id];
      }

      for (const id of missingOrOutdated) {
        const seg = fetched[id];
        if (!seg || typeof seg !== "object") {
          delete merged[id];
          continue;
        }
        const safeUpdatedAt = parseIndexValue(indexVal[id]) || toSegmentStamp(seg, remoteUpdatedAt);
        merged[id] = {
          ...seg,
          id: String(seg.id || id),
          updatedAt: safeUpdatedAt,
        };
      }

      const mergedSegments = Object.values(merged).sort((a, b) => {
        const aTs = toSegmentStamp(a, 0);
        const bTs = toSegmentStamp(b, 0);
        if (aTs !== bTs) return aTs - bTs;
        return String(a?.id || "").localeCompare(String(b?.id || ""));
      });

      const next = normalizeNavGrid({
        ...local,
        spacingMeters: toPositiveNumber(metaVal.spacingMeters, local.spacingMeters || 50),
        mergeMeters: toPositiveNumber(metaVal.mergeMeters, local.mergeMeters || 15),
        routeMergeMeters: toPositiveNumber(
          metaVal.routeMergeMeters,
          Math.max(toPositiveNumber(metaVal.mergeMeters, local.mergeMeters || 15), 30)
        ),
        entryPromptMeters: Number(metaVal.entryPromptMeters) || local.entryPromptMeters || 200,
        updatedAt: retainedLocalIds.length
          ? Math.max(remoteUpdatedAt, localStamp)
          : remoteUpdatedAt || Date.now(),
        segments: mergedSegments,
      });

      if (retainedLocalIds.length) {
        navGridLog("warn", "retained unsynced local segments", {
          count: retainedLocalIds.length,
          ids: retainedLocalIds.slice(0, 10),
          remoteUpdatedAt,
          localUpdatedAt: localStamp,
        });
      }

      return {
        grid: next,
        changed: true,
        source: "incremental",
          stats: {
            fetchedSegments: missingOrOutdated.length,
            removedSegments: removedIds.length,
            retainedLocalSegments: retainedLocalIds.length,
          },
      };
    }
  } catch (err) {
    navGridLog("warn", "incremental fetch failed; fallback legacy", {
      error: String(err?.message || err || "unknown"),
    });
    console.warn("⚠️ fetchNavGridFromCloud incremental falhou, fallback legacy:", err);
  }

  const legacy = await fetchLegacyGrid(db);
  if (!legacy) {
    return { grid: null, changed: false, source: "none", stats: null };
  }

  const changed =
    force ||
    toPositiveNumber(legacy.updatedAt, 0) > localStamp ||
    (Array.isArray(local.segments) ? local.segments.length : 0) === 0;

  return {
    grid: legacy,
    changed,
    source: "legacy",
    stats: { fetchedSegments: Array.isArray(legacy.segments) ? legacy.segments.length : 0, removedSegments: 0 },
  };
}
